import { useState } from 'react';
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import {
    AnimatedLoader,
    HorizontalFlex,
    Input,
    PadContainer,
    SingleSelect,
    VerticalFlex,
    ErrorAlert,
} from 'smartsuite-ui';
import { supportedCountriesEnglish } from '../constants/countries';
import { airlines } from '../constants/flightEntryConstants';
import { resorts } from '../constants/resorts';
import { useTranslation } from 'react-i18next';

import { useMutation, useQuery } from '@tanstack/react-query';
import {
    closeEnrollmentRequestUri,
    getEnrollmentRequestUri,
    openEnrollmentRequestUri,
    updateEnrollmentRequestUri,
} from '../constants/apiConstants';
import { putDataPromise, postDataPromise } from 'brock-fetch-utils';
import {
    isEmailValidFormat,
    isPhoneValid,
    isNumeric,
    isAlphaSpaceHyphen,
    isAddressValid,
    isArrivalDateValid,
    isPnrValid,
} from '../utils/validationUtil';
import { formatPhoneNumber } from '../utils/formatters';
import NumberInput from '../components/NumberInput';
import LoadingButton from '../components/LoadingButton';

function EnrollmentRequestPage() {
    const { t } = useTranslation();
    const { enrollmentRequestId } = useParams();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [hasServerError, setServerError] = useState(false);

    const enrollmentRequestQuery = useQuery({
        queryKey: ['enrollmentRequest', enrollmentRequestId],
        queryFn: async () => {
            const response = await fetch(
                getEnrollmentRequestUri(enrollmentRequestId),
            );

            if (!response.ok) {
                navigate('..');
            }
            const parsedResponse = await response.json();

            return parsedResponse;
        },
        onSuccess: (data) => {
            setEnrollmentRequestData({
                ...emptyEnrollmentRequestData,
                ...data,
            });
        },
        enabled: !!enrollmentRequestId,
    });

    const openEnrollmentRequestMutation = useMutation({
        mutationFn: (payload) =>
            postDataPromise(openEnrollmentRequestUri(), payload),
        onMutate: () => {
            setServerError(false);
        },
        onSuccess: (data) => {
            setServerError(false);
            navigate(`./${data.enrollmentRequestId}`);
        },
        onError: (error) => {
            setServerError(true);
        },
    });

    const updateEnrollmentRequestMutation = useMutation({
        mutationFn: (payload) =>
            putDataPromise(updateEnrollmentRequestUri(), payload),
        onMutate: () => {
            setServerError(false);
        },
        onSuccess: (data) => {
            setServerError(false);
            navigate(`./${enrollmentRequestId}`);
        },
        onError: (error) => {
            setServerError(true);
        },
    });

    const closeEnrollmentRequestMutation = useMutation({
        mutationFn: (payload) =>
            postDataPromise(
                closeEnrollmentRequestUri(),
                payload.enrollmentRequestId,
            ),
        onMutate: () => {
            setServerError(false);
        },
        onSuccess: (data) => {
            setServerError(false);
            navigate(`./${enrollmentRequestId}`);
        },
        onError: (error) => {
            setServerError(true);
        },
    });

    const emptyEnrollmentRequestData = {
        reservationId: '',
        fullName: '',
        airlineCode: '',
        resortName: '',
        pnr: '',
        arrivalDateLocal: '',
        numberOfPassengers: 0,
        numberOfBags: 0,
        mailingAddress: '',
        emailAddress: '',
        phoneNumber: '',
    };

    const initialEnrollmentRequestData = {
        ...emptyEnrollmentRequestData,
        reservationId: searchParams.get('reservationId')?.trim() || '',
        fullName: searchParams.get('lastName')?.trim() || '',
        arrivalDateLocal: searchParams.get('arrivalDate')?.trim() || '',
    };

    const [enrollmentRequestData, setEnrollmentRequestData] = useState(
        initialEnrollmentRequestData,
    );

    const isEnrollmentRequestOpened =
        enrollmentRequestQuery?.data?.closedTimestamp === null;

    const isEnrollmentRequestClosed =
        !!enrollmentRequestQuery?.data?.closedTimestamp;

    const isEditEnrollmentPath =
        !!enrollmentRequestId &&
        pathname.includes(`/enrollmentRequest/${enrollmentRequestId}/edit`);

    const handleCreateNewEnrollmentRequestClick = (event) => {
        event.preventDefault();
        setServerError(false);
        setEnrollmentRequestData(emptyEnrollmentRequestData);
        navigate('/waltdisneyworld/enrollmentRequest');
    };

    const handleEnrollmentRequestDataChange = (field, value) => {
        setEnrollmentRequestData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    function isFullNameValid() {
        return (
            !!enrollmentRequestData?.fullName &&
            isAlphaSpaceHyphen(enrollmentRequestData?.fullName)
        );
    }

    function isAirlineCodeValid() {
        return (
            !!enrollmentRequestData?.airlineCode &&
            isAlphaSpaceHyphen(enrollmentRequestData?.airlineCode)
        );
    }

    function isResortNameValid() {
        return (
            !!enrollmentRequestData?.resortName &&
            isAlphaSpaceHyphen(enrollmentRequestData?.resortName)
        );
    }

    function isFlightConfirmationNumberValid() {
        return (
            !!enrollmentRequestData?.pnr &&
            isPnrValid(enrollmentRequestData?.pnr)
        );
    }

    function isArrivalDateLocalValid() {
        return (
            !!enrollmentRequestData?.arrivalDateLocal &&
            isArrivalDateValid(enrollmentRequestData?.arrivalDateLocal)
        );
    }

    function isNumberOfBagsValid() {
        return (
            // checks if numberOfBags is a number, is an integer, not NaN, and not infinity
            Number.isInteger(enrollmentRequestData?.numberOfBags)
        );
    }

    function isMailingAddressValid() {
        return (
            !!enrollmentRequestData?.mailingAddress &&
            isAddressValid(enrollmentRequestData?.mailingAddress)
        );
    }

    function isEmailAddressValid() {
        return (
            !!enrollmentRequestData?.emailAddress &&
            isEmailValidFormat(enrollmentRequestData?.emailAddress)
        );
    }

    function isPhoneNumberValid() {
        return (
            !!enrollmentRequestData?.phoneNumber &&
            isPhoneValid(enrollmentRequestData?.phoneNumber)
        );
    }

    const canSubmitEnrollmentRequest =
        isFullNameValid() &&
        isAirlineCodeValid() &&
        isResortNameValid() &&
        isFlightConfirmationNumberValid() &&
        isArrivalDateLocalValid() &&
        isNumberOfBagsValid() &&
        isMailingAddressValid() &&
        isEmailAddressValid() &&
        isPhoneNumberValid();

    const getEnrollmentRequestPayload = () => ({
        ...enrollmentRequestData,
        numberOfPassengers:
            Number.parseInt(enrollmentRequestData?.numberOfPassengers, 10) || 0,
        numberOfBags:
            Number.parseInt(enrollmentRequestData?.numberOfBags, 10) || 0,
    });

    return (
        <div className="page-wrapper">
            <div className="enrollment-request-card">
                <header className="enrollment-request-card__header">
                    <div className="enrollment-request-card__title">
                        Enrollment Request{' '}
                        {isEnrollmentRequestOpened && 'Submitted!'}
                    </div>
                </header>
                {isEnrollmentRequestOpened && (
                    <div>Your enrollment request has been submitted.</div>
                )}
                {isEnrollmentRequestClosed && (
                    <div>
                        <div>
                            This enrollment request has been closed.{' '}
                            <a
                                href="/waltdisneyworld/enrollmentRequest"
                                onClick={handleCreateNewEnrollmentRequestClick}
                            >
                                Click here
                            </a>{' '}
                            to create a new enrollment request.
                        </div>
                    </div>
                )}

                <div className="enrollment-request-card__shared-fields-container">
                    <Input
                        label={`${t('fullNameRequired')}`}
                        className="input"
                        id="fullName"
                        value={enrollmentRequestData?.fullName}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'fullName',
                                event.target.value,
                            )
                        }
                        isValid={
                            !enrollmentRequestData?.fullName ||
                            isFullNameValid()
                        }
                        errorMessage={t('fullNameInvalid')}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <SingleSelect
                        cssClassModifiers={['small-relative']}
                        label={t('airlineLabel')}
                        options={airlines}
                        value={airlines.find(
                            (option) =>
                                option.airlineCode ===
                                enrollmentRequestData?.airlineCode,
                        )}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'airlineCode',
                                event?.airlineCode,
                            )
                        }
                        placeholder={t('selectAirline')}
                        textField="airlineName"
                        valueField="airlineCode"
                        isValid={
                            !enrollmentRequestData.airlineCode ||
                            isAirlineCodeValid()
                        }
                        errorMessage={t('airlineInvalid')}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <SingleSelect
                        cssClassModifiers={['small-relative']}
                        label={t('resortNameRequired')}
                        options={resorts}
                        value={resorts.find(
                            (option) =>
                                option.valueField ===
                                enrollmentRequestData?.resortName,
                        )}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'resortName',
                                event?.valueField,
                            )
                        }
                        textField="textField"
                        valueField="valueField"
                        isValid={
                            !enrollmentRequestData.resortName ||
                            isResortNameValid()
                        }
                        errorMessage={t('resortInvalid')}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <Input
                        label={t('pnrLabel')}
                        placeholder={t('pnrPlaceholder')}
                        value={enrollmentRequestData?.pnr}
                        maxLength={6}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'pnr',
                                event.target.value.toUpperCase(),
                            )
                        }
                        errorMessage={t('pnrInvalid')}
                        isValid={
                            !enrollmentRequestData.pnr ||
                            isFlightConfirmationNumberValid()
                        }
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <Input
                        label={t('reservationNumber')}
                        placeholder={t('reservationNumber')}
                        value={enrollmentRequestData?.reservationId}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'reservationId',
                                event.target.value,
                            )
                        }
                        disabled={isEnrollmentRequestClosed}
                    />
                    <Input
                        label={t('arrivalDateRequired')}
                        className="input"
                        type="date"
                        value={enrollmentRequestData?.arrivalDateLocal}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'arrivalDateLocal',
                                event.target.value,
                            )
                        }
                        isValid={
                            !enrollmentRequestData?.arrivalDateLocal ||
                            isArrivalDateLocalValid()
                        }
                        errorMessage={
                            enrollmentRequestData?.arrivalDateLocal &&
                            t('arrivalDateInvalid')
                        }
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <NumberInput
                        id={'numberOfPassengers'}
                        label={t('numberOfPassengersOptional')}
                        value={enrollmentRequestData?.numberOfPassengers ?? ''}
                        onChange={(event) => {
                            handleEnrollmentRequestDataChange(
                                'numberOfPassengers',
                                event.target.value === ''
                                    ? null
                                    : Number(event.target.value),
                            );
                        }}
                        disabled={isEnrollmentRequestClosed}
                        minNumber={0}
                        maxNumber={99}
                    />
                    <NumberInput
                        id={'numberOfBags'}
                        label={t('numberOfBagsRequired')}
                        value={enrollmentRequestData?.numberOfBags ?? ''}
                        onChange={(event) => {
                            handleEnrollmentRequestDataChange(
                                'numberOfBags',
                                event.target.value === ''
                                    ? null
                                    : Number(event.target.value),
                            );
                        }}
                        isValid={isNumberOfBagsValid()}
                        errorMessage={t('numberOfBagsInvalid')}
                        minNumber={0}
                        maxNumber={99}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <Input
                        label={t('addressRequired')}
                        className="input"
                        value={enrollmentRequestData?.mailingAddress}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'mailingAddress',
                                event.target.value,
                            )
                        }
                        isValid={
                            !enrollmentRequestData?.mailingAddress ||
                            isMailingAddressValid()
                        }
                        errorMessage={
                            enrollmentRequestData?.mailingAddress &&
                            t('mailingAddressInvalid')
                        }
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <div className="contact-information">
                    <div className="contact-information__title">
                        {t('contactInformationTitle')}
                    </div>
                    <p>{t('contactInformationDescription')}</p>
                    <Input
                        label={t('emailAddressRequired')}
                        id="email"
                        value={enrollmentRequestData.emailAddress}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'emailAddress',
                                event.target.value,
                            )
                        }
                        isValid={
                            !enrollmentRequestData.emailAddress ||
                            isEmailAddressValid()
                        }
                        errorMessage={t('emailAddressInvalid')}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                    <Input
                        label={t('phoneNumberRequired')}
                        id="phoneNumber"
                        value={formatPhoneNumber(
                            enrollmentRequestData.phoneNumber,
                        )}
                        onChange={(event) =>
                            handleEnrollmentRequestDataChange(
                                'phoneNumber',
                                event.target.value.replace(/[^\d]/g, ''),
                            )
                        }
                        isValid={
                            !enrollmentRequestData.phoneNumber ||
                            isPhoneNumberValid()
                        }
                        errorMessage={t('phoneNumberInvalid')}
                        type="tel"
                        placeholder="(###) ###-####"
                        maxLength={14}
                        required
                        disabled={isEnrollmentRequestClosed}
                    />
                </div>
                <PadContainer cssClassModifiers={['pad-small']} />
                <HorizontalFlex cssClassModifiers={['justify-center']}>
                    {!isEnrollmentRequestClosed &&
                        (isEnrollmentRequestOpened ? (
                            <LoadingButton
                                className="btn btn--primary"
                                isLoading={
                                    updateEnrollmentRequestMutation.isLoading
                                }
                                onClick={() =>
                                    updateEnrollmentRequestMutation.mutate(
                                        getEnrollmentRequestPayload(),
                                    )
                                }
                                disabled={
                                    !canSubmitEnrollmentRequest ||
                                    updateEnrollmentRequestMutation.isLoading
                                }
                            >
                                <span>Update Request</span>
                            </LoadingButton>
                        ) : (
                            <LoadingButton
                                className="btn btn--primary"
                                isLoading={
                                    openEnrollmentRequestMutation.isLoading
                                }
                                onClick={() =>
                                    openEnrollmentRequestMutation.mutate(
                                        getEnrollmentRequestPayload(),
                                    )
                                }
                                disabled={
                                    !canSubmitEnrollmentRequest ||
                                    openEnrollmentRequestMutation.isLoading
                                }
                            >
                                <span>Submit Request</span>
                            </LoadingButton>
                        ))}
                </HorizontalFlex>
            </div>
            <PadContainer cssClassModifiers={['pad-small']} />
            {isEnrollmentRequestOpened && isEditEnrollmentPath && (
                <HorizontalFlex cssClassModifiers={['justify-center']}>
                    <span>
                        <a
                            href={`/waltdisneyworld/enrollmentRequest/${enrollmentRequestId}`}
                            onClick={(event) => {
                                event.preventDefault();
                                closeEnrollmentRequestMutation.mutate(
                                    { enrollmentRequestId },
                                    {
                                        onSuccess: async () => {
                                            await enrollmentRequestQuery.refetch();
                                        },
                                    },
                                );
                            }}
                        >
                            Click here
                        </a>{' '}
                        to close your enrollment request
                    </span>
                </HorizontalFlex>
            )}
            {hasServerError && (
                <div className="enrollment-request-error">
                    <ErrorAlert type="error">
                        <strong className="error-title">Error</strong>
                        <div className="error-body">
                            We are having trouble processing your request right
                            now. Please try again.
                        </div>
                    </ErrorAlert>
                </div>
            )}
        </div>
    );
}

export default EnrollmentRequestPage;
